import React, { useEffect, useState } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import config from '../../config';

const colors = { media: '#3563ef', target: '#ffbd0b' };
const initialZoom = 8;
const initialCenter = { lat: 25.7617, lng: -80.1918 };
const pinWidth = 28;
const pinHeight = 36;
const validPoint = point => Number.isFinite(point.latitude) && Number.isFinite(point.longitude) &&
  point.latitude >= -90 && point.latitude <= 90 && point.longitude >= -180 && point.longitude <= 180 &&
  (point.latitude !== 0 || point.longitude !== 0);

// A 50 km neighborhood keeps nearby city sites together without joining distant regions.
const neighborhoodRadiusKm = 50;
const distanceKm = (a, b) => {
  const radians = degrees => degrees * Math.PI / 180;
  const latitudeDelta = radians(b.latitude - a.latitude);
  const longitudeDelta = radians(b.longitude - a.longitude);
  const haversine = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(radians(a.latitude)) * Math.cos(radians(b.latitude)) * Math.sin(longitudeDelta / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(Math.min(1, haversine)));
};

export const getAttributionMapCenter = points => {
  const validPoints = points.filter(validPoint);
  let selected = validPoints[0];
  let largestCount = 0;
  validPoints.forEach(candidate => {
    const count = validPoints.filter(point => distanceKm(candidate, point) <= neighborhoodRadiusKm).length;
    // Strict comparison preserves input order when distant neighborhoods have equal counts.
    if (count > largestCount) {
      selected = candidate;
      largestCount = count;
    }
  });
  return selected ? { lat: selected.latitude, lng: selected.longitude } : initialCenter;
};

export const groupAttributionMapSites = sites => {
  const groups = [];
  sites.filter(validPoint).forEach(site => {
    const group = groups.find(item => item.entries.some(entry =>
      (site.siteId && entry.siteId && site.siteId === entry.siteId) ||
      (Math.abs(site.latitude - entry.latitude) < 0.00001 &&
        Math.abs(site.longitude - entry.longitude) < 0.00001)));
    if (group) group.entries.push(site);
    else groups.push({ entries: [site] });
  });
  return groups.map(group => {
    const target = group.entries.find(entry => entry.kind === 'target');
    const location = target || group.entries[0];
    const kinds = new Set(group.entries.map(entry => entry.kind));
    return {
      ...location,
      entries: group.entries,
      label: [...new Set(group.entries.map(entry => entry.label))].join(' / '),
      kind: kinds.size > 1 ? 'both' : location.kind
    };
  });
};

const svgNamespace = 'http://www.w3.org/2000/svg';
const pinOutline = 'M20 1.5C10 1.5 2 9.5 2 20c0 13.4 18 30 18 30s18-16.6 18-30C38 9.5 30 1.5 20 1.5Z';
const pinBody = 'M20 4C11.3 4 4.5 11.1 4.5 20.2c0 11.2 15.5 26.5 15.5 26.5s15.5-15.3 15.5-26.5C35.5 11.1 28.7 4 20 4Z';
const pinLeft = 'M20 4C11.3 4 4.5 11.1 4.5 20.2c0 11.2 15.5 26.5 15.5 26.5V4Z';

const svgElement = (name, attributes) => {
  const element = document.createElementNS(svgNamespace, name);
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
  return element;
};

const pinElement = kind => {
  const pin = svgElement('svg', { viewBox: '0 0 40 52', width: pinWidth, height: pinHeight,
    class: 'store-attribution-pin', 'aria-hidden': 'true' });
  pin.appendChild(svgElement('path', { d: pinOutline, fill: '#fff', stroke: '#35456a', 'stroke-width': 0.8 }));
  pin.appendChild(svgElement('path', { d: pinBody, fill: colors[kind === 'both' ? 'media' : kind] }));
  if (kind === 'both') pin.appendChild(svgElement('path', { d: pinLeft, fill: colors.target }));
  pin.appendChild(svgElement('circle', { cx: 20, cy: 20, r: 8.5, fill: '#fff' }));
  pin.appendChild(svgElement('circle', { cx: 20, cy: 20, r: 6.5, fill: '#14233d' }));
  return pin;
};

const StoreAttributionMap = ({ sites }) => {
  const [map, setMap] = useState(null);

  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return undefined;
    const points = groupAttributionMapSites(sites);
    let infoWindow;
    const markers = points.map(point => {
      const pin = pinElement(point.kind);
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: point.latitude, lng: point.longitude },
        content: pin,
        title: point.label
      });
      marker.addListener('click', () => {
        infoWindow?.close();
        const content = document.createElement('div');
        point.entries.forEach((entry, index) => {
          const section = document.createElement('div');
          if (index > 0) section.style.marginTop = '8px';
          const title = document.createElement('strong');
          title.textContent = entry.label;
          section.appendChild(title);
          const detail = document.createElement('div');
          detail.textContent = `${entry.kind === 'target' ? 'Attributed visitors' : 'Exposed devices'}: ` +
            entry.value.toLocaleString();
          section.appendChild(detail);
          content.appendChild(section);
        });
        infoWindow = new window.google.maps.InfoWindow({ content });
        infoWindow.open({ anchor: marker, map });
      });
      return marker;
    });

    map.setCenter(getAttributionMapCenter(points));
    map.setZoom(initialZoom);
    return () => {
      infoWindow?.close();
      markers.forEach(marker => { marker.map = null; });
    };
  }, [map, sites]);

  return (
    <div className="store-attribution-map-canvas">
      <div className="store-attribution-map-legend">
        <span><i className="media" />Media Owner Sites</span>
        <span><i className="target" />Target Sites</span>
        <span><i className="both" />Both</span>
      </div>
      <GoogleMap
        zoom={initialZoom}
        center={initialCenter}
        options={{ disableDefaultUI: true, zoomControl: true, mapId: config.mapsApi.mapId }}
        mapContainerClassName="store-attribution-google-map"
        onLoad={setMap}
      />
    </div>
  );
};

export default StoreAttributionMap;
